import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
  GetManyParams,
  GetManyResult,
  DeleteParams,
} from 'react-admin'
import { PRINT_TEXT_ITEMS_URL } from '../../api-urls'
import {
  get,
  getWithPagination,
  patch,
  post,
  remove,
} from '../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import { printTextItemDto } from '../../dto/print-text-item.dto'
import filterMapper from './print-text-items-filter.mapper'
import { mapSortPrintTextItemsParam } from './print-text-items-sort.mapper'
import { PrintAnchor } from '../../enum/PrintAnchor'
import { PrintFontStyle } from '../../enum/PrintFontStyle'
import { PrintParameterTypes } from '../../enum/PrintParameterTypes'
import printItemParameterProvider from '../print-item-parameters/print-item-parameters.provider'
import { ResourceName } from '../../ResourceName'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<printTextItemDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortPrintTextItemsParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<printTextItemDto[]>(
      `${PRINT_TEXT_ITEMS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<printTextItemDto>> => {
    const data = await get<printTextItemDto>(PRINT_TEXT_ITEMS_URL, `/${id}`)
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource,
    { data }: CreateParams<CreatePrintTextItemRequest>,
  ): Promise<CreateResult<printTextItemDto>> => {
    const hexColor = data.hexColor ?? '#000000'
    const response = await post<
      CreatePrintTextItemRequest[],
      printTextItemDto[]
    >(PRINT_TEXT_ITEMS_URL, [{ ...data, hexColor }])
    const printTextItem = response[0]
    if (data.newParamKey && data.newParamType && printTextItem.id)
      await printItemParameterProvider.create(
        ResourceName.PRINT_ITEM_PARAMETERS,
        {
          data: {
            key: data.newParamKey,
            options: data?.newParamOptions,
            type: data.newParamType,
            printItemId: printTextItem.id,
          },
        },
      )
    return {
      data: printTextItem,
    }
  },
  update: async (
    resource: string,
    { id, data }: UpdateParams,
  ): Promise<UpdateResult> => {
    const hexColor = data.hexColor ?? '#000000'
    const update = await patch<
      { id: Identifier; [n: string]: any },
      printTextItemDto
    >(PRINT_TEXT_ITEMS_URL, { id, ...data, hexColor })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult> => {
    const deleted = await remove<printTextItemDto>(
      PRINT_TEXT_ITEMS_URL,
      `/Delete/${id}`,
    )

    return Promise.resolve({ data: deleted })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<printTextItemDto>> => {
    const data = await get<printTextItemDto[]>(
      PRINT_TEXT_ITEMS_URL,
      `/GetByConditions/o=>new int[] {${ids.toString()}}.Contains(o.Id)`,
    )
    return Promise.resolve({ data })
  },
} as DataProvider

export interface CreatePrintTextItemRequest {
  readonly xPosition: number
  readonly yPosition: number
  readonly anchor: PrintAnchor
  readonly priority: number
  readonly printLayoutId: number
  readonly text: string
  readonly fontSize: number
  readonly fontName: string
  readonly fontStyle: PrintFontStyle
  readonly hexColor: string
  readonly newParamKey: string
  readonly newParamOptions: string
  readonly newParamType: PrintParameterTypes
}

export default provider
